// Alert.ts - TypeScript type definitions for Alert entity

// Type aliases for commonly used union types
export type ComparisonOperator = '>' | '<' | '=' | '!=' | '>=' | '<=';
export type AlertSensitivity = 'threshold-crossed' | 'value-changed' | 'one-per-day';

export interface Alert {
  // Primary key
  AlertId: number;
  
  // Foreign keys
  CreatedBy: number;
  
  // Basic alert information
  AlertDescription?: string | null;
  AlertTitle: string;
  CreatedAt: Date;
  LastUpdatedAt?: Date;
  LastTriggeredAt?: Date | null;
  
  // Status flags
  IsActive: boolean;
  IsPublic: boolean;
  
  // Notification settings
  NotificationChannels: string;
  NotificationEmails: string;
  RevealBiPayload_JSON: string;
  
  // Threshold and comparison settings
  ComparisonOperator?: ComparisonOperator | null;
  ThresholdValue?: number | null;
  AlertSensitivity?: AlertSensitivity | null;
  MetricName?: string | null;
  
  // Tracking fields
  LastTriggeredValue?: number | null;
  PreviousValue?: number | null;
  LastNotificationSentAt?: Date | null;
}

// Type for creating a new alert (without auto-generated fields)
export interface CreateAlertRequest {
  AlertDescription?: string;
  AlertTitle: string;
  NotificationChannels: string;
  NotificationEmails: string;
  RevealBiPayload_JSON: string;
  IsPublic?: boolean;
  ComparisonOperator?: ComparisonOperator | null;
  ThresholdValue?: number | null;
  AlertSensitivity?: AlertSensitivity | null;
  MetricName: string;
}

// Type for updating an existing alert
export interface UpdateAlertRequest {
  AlertId: number;
  AlertDescription?: string;
  AlertTitle?: string;
  NotificationChannels?: string;
  NotificationEmails?: string;
  RevealBiPayload_JSON?: string;
  IsPublic?: boolean;
  IsActive?: boolean;
  ComparisonOperator?: ComparisonOperator | null;
  ThresholdValue?: number | null;
  AlertSensitivity?: AlertSensitivity | null;
  MetricName?: string | null;
}

// Type for updating alert status (activate/deactivate)
export interface UpdateAlertStatusRequest {
  AlertId: number;
  isActive: boolean;
}

// Type for alert with user information (from JOIN with Users table)
export interface AlertWithUser extends Alert {
  CreatedByName?: string;
}

// Type for filtering options
export interface AlertFilterOptions {
  CreatedByName?: string;
  IsPublic?: boolean;
  AlertTitle?: string;
  DateFrom?: Date;
  DateTo?: Date;
  LastTriggeredFrom?: Date;
  LastTriggeredTo?: Date;
  AlertSensitivity?: AlertSensitivity;
  MetricName?: string;
  ComparisonOperator?: ComparisonOperator;
  ThresholdValueMin?: number;
  ThresholdValueMax?: number;
  LastNotificationSentFrom?: Date;
  LastNotificationSentTo?: Date;
}

// Type for ordering options
export interface AlertOrderOptions {
  field:
    | 'CreatedAt'
    | 'LastUpdatedAt'
    | 'LastTriggeredAt'
    | 'AlertTitle'
    | 'CreatedBy'
    | 'IsPublic'
    | 'IsActive'
    | 'LastNotificationSentAt'
    | 'ComparisonOperator'
    | 'ThresholdValue'
    | 'AlertSensitivity'
    | 'MetricName'
    | 'LastTriggeredValue';
  direction: 'ASC' | 'DESC';
}

// Type for pagination options
export interface AlertPaginationOptions {
  limit?: number;
  offset?: number;
}

// Type for complete query options
export interface AlertQueryOptions extends AlertFilterOptions {
  orderBy?: AlertOrderOptions;
  limit?: number;
  offset?: number;
}

// Type for API responses
export interface AlertResponse {
  message: string;
  alert?: Alert | AlertWithUser;
  alerts?: AlertWithUser[];
  count?: number;
  AlertId?: number;
  rowsAffected?: number;
  error?: string;
}

// Type for alert creation response
export interface CreateAlertResponse {
  message: string;
  AlertId: number;
}

// Type for alert update/delete response
export interface UpdateAlertResponse {
  message: string;
  rowsAffected: number;
}

// Type for alert list response
export interface AlertListResponse {
  message: string;
  alerts: AlertWithUser[];
  count: number;
}

// Type for alert count response
export interface AlertCountResponse {
  message: string;
  count: number;
}

// Constants for validation
export const COMPARISON_OPERATORS = ['>', '<', '=', '!=', '>=', '<='] as const;
export const ALERT_SENSITIVITY_TYPES = ['threshold-crossed', 'value-changed', 'one-per-day'] as const;

// Comparison operator options with labels
export const comparisonOperators = [
  { value: '>', label: 'Greater than (>)' },
  { value: '<', label: 'Less than (<)' },
  { value: '=', label: 'Equals (=)' },
  { value: '!=', label: 'Not equals (!=)' },
  { value: '>=', label: 'Greater than or equal (>=)' },
  { value: '<=', label: 'Less than or equal (<=)' },
] as const;

// Type guards
export const isValidComparisonOperator = (
  operator: string | null | undefined,
): operator is ComparisonOperator => {
  return typeof operator === 'string' && COMPARISON_OPERATORS.includes(operator as ComparisonOperator);
};

export const isValidAlertSensitivity = (
  sensitivity: string | null | undefined,
): sensitivity is AlertSensitivity => {
  return typeof sensitivity === 'string' && ALERT_SENSITIVITY_TYPES.includes(sensitivity as AlertSensitivity);
};

// Utility types (moved to top of file)
